import { useIsMutating } from "@tanstack/react-query"
import { ImageIcon, Save, Trash2 } from "lucide-react"
import { useStore } from "zustand"

import { Button } from "@/components/button"
import { CardActionBar } from "@/components/shared/card-action-bar"
import { CardHeading } from "@/components/shared/card-heading"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  DELETE_BOSS_MUTATION_KEY,
  useUpdateBossMutation,
  type BossDraft,
} from "@/hooks/talent-config"
import {
  useTalentConfigRouteStore,
  setPageError,
  setDeleteTarget,
  updateBossDraftField,
  setSelectedBossId,
} from "@/lib/stores/talent-config-ui-store"
import {
  type TalentConfigBoss,
  type TalentConfigSnapshot,
} from "@/lib/talent-config-api"

type BossValidation = {
  name: string | null
}

export function BossProfileCard({
  boss,
  snapshot,
}: {
  boss: TalentConfigBoss
  snapshot: TalentConfigSnapshot | undefined
}) {
  const store = useTalentConfigRouteStore()
  const bossDraft = useStore(store, (s) => s.bossDraft)
  const pageError = useStore(store, (s) => s.pageError)

  const updateBossMutation = useUpdateBossMutation(store)
  const isDeletingBoss =
    useIsMutating({ mutationKey: DELETE_BOSS_MUTATION_KEY }) > 0

  const currentBossValidation = validateBossDraft(bossDraft, snapshot)

  if (!bossDraft) {
    return null
  }

  const isDirty = isBossDraftDirty(bossDraft, boss)
  const hasErrors = Boolean(currentBossValidation?.name)

  return (
    <Card>
      <CardHeader>
        <CardHeading icon={<ImageIcon className="size-5 text-primary" />}>
          Boss Profile
        </CardHeading>
        <CardDescription>
          Edit top-level metadata that powers browsing, sorting, and future
          visual surfaces.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-2 md:col-span-2">
          <Label htmlFor="boss-name">Boss Name</Label>
          <Input
            id="boss-name"
            value={bossDraft.name}
            onChange={(event) =>
              updateBossDraftField(store, "name", event.target.value)
            }
          />
          {currentBossValidation?.name ? (
            <p className="text-xs text-destructive">
              {currentBossValidation.name}
            </p>
          ) : null}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="boss-season">Season</Label>
          <Input id="boss-season" value={bossDraft.season} readOnly />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="boss-sort-order">Sort Order</Label>
          <Input
            id="boss-sort-order"
            type="number"
            min={1}
            value={String(bossDraft.sortOrder)}
            onChange={(event) =>
              updateBossDraftField(
                store,
                "sortOrder",
                Math.max(1, Number(event.target.value || 1)),
              )
            }
          />
        </div>
        <CardActionBar className="items-center gap-3 md:col-span-2">
          <Button
            disabled={updateBossMutation.isPending || !isDirty || hasErrors}
            onClick={() => {
              updateBossMutation.mutate({
                bossId: boss.id,
                draft: bossDraft,
              })
            }}
            type="button"
          >
            <Save className="size-4" />
            {updateBossMutation.isPending ? "Saving boss..." : "Save Boss"}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isDeletingBoss}
            onClick={() => {
              setDeleteTarget(store, {
                kind: "boss",
                id: boss.id,
                name: boss.name,
              })
            }}
          >
            <Trash2 className="size-4" />
            {isDeletingBoss ? "Deleting boss..." : "Delete Boss"}
          </Button>
          {pageError ? (
            <span className="text-sm text-destructive">{pageError}</span>
          ) : null}
        </CardActionBar>
      </CardContent>
    </Card>
  )
}

// Helper functions
function validateBossDraft(
  draft: BossDraft | null,
  snapshot: TalentConfigSnapshot | undefined,
): BossValidation | null {
  if (!draft) {
    return null
  }

  const name = draft.name.trim()

  const nameError =
    name.length === 0
      ? "Boss name is required."
      : name.length < 2
        ? "Boss name should be at least 2 characters."
        : null

  return {
    name: nameError,
  }
}

function isBossDraftDirty(draft: BossDraft, boss: TalentConfigBoss): boolean {
  return (
    draft.season !== boss.season ||
    draft.name !== boss.name ||
    draft.sortOrder !== boss.sortOrder
  )
}
